import React, { useMemo, useEffect, useCallback, useState } from "react";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
  useWatch,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { company, contact, product, paymentTop, paymentDates } from "./config";
import { mspFormSchema, FormValues } from "./schema";
import type { ProductItem, PlanItem, PlanPriceItem } from "../services/types";
import {
  SECTION_CARD_CLASS,
  MSP_DEFAULT_VALUES,
} from "../../../constants/mspAndCustomerForm";
import {
  getTimezoneOptionsForCountry,
  iso2FromCountryName,
} from "../../../utils/timezones";
import { AddMspFormProps } from "./types";
import {
  useRegionList,
  useCountryList,
  useStateList,
  useCityList,
} from "../../../hooks/useLocationData";
import { useFieldConfig } from "../../../hooks/useFieldConfig";
import {
  createMsp,
  fetchProducts,
  fetchMSPPlans,
} from "../services/mspService";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setLoading,
  setError,
  setNotification,
  clearError,
  clearNotification,
} from "../slices/mspSlice";
import { FormHeader } from "../../../components/form/FormHeader";
import { calcExpirationDate } from "../../../utils/calculations";
import PaymentDetails from "../../../components/PaymentDetails";
import FormSection from "../../../components/FormSection";

// utils
const trim = (v: string) => v.trim();
const opt = (v?: string) => v?.trim() ?? "";
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function AddMspForm({
  onCancel,
  onSuccess,
}: Readonly<AddMspFormProps>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, notification } = useAppSelector((s) => s.msp);

  // ---------- Local state ----------
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [pricingOptions, setPricingOptions] = useState<PlanPriceItem[]>([]);
  const [productLoading, setProductLoading] = useState({
    products: false,
    plans: false,
  });
  const [licenses, setLicenses] = useState<number | null>(null);

  const methods = useForm<FormValues>({
    resolver: zodResolver(mspFormSchema) as Resolver<FormValues>,
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: MSP_DEFAULT_VALUES,
    shouldUnregister: false,
  });

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState,
    setValue,
    trigger,
    watch,
    formState: { isSubmitting },
  } = methods;

  // watch fields for deps
  const companyCountryId = watch("company_country_id");
  const companyStateId = watch("company_state_id");
  const contactCountryId = watch("country_id");
  const contactStateId = watch("state_id");

  const [
    selectedProductId,
    selectedPlanId,
    discount,
    billingFrequency,
    paymentDate,
  ] = useWatch<
    FormValues,
    ["product", "plan", "discount", "billing_frequency", "date_of_payment"]
  >({
    control,
    name: [
      "product",
      "plan",
      "discount",
      "billing_frequency",
      "date_of_payment",
    ],
  });

  const totalPaid = useWatch({ control, name: "total_paid_amount" });

  // ---------- Location hooks ----------
  const {
    regionList: regions,
    isRegionLoading,
    regionLoadingError,
  } = useRegionList(companyCountryId ? Number(companyCountryId) : undefined);
  const {
    countryList: countries,
    isCountryLoading,
    countryLoadingError,
  } = useCountryList();

  const {
    stateList: companyStates,
    isStateLoading: loadingCompanyStates,
    stateLoadingError: companyStateError,
  } = useStateList(companyCountryId);

  const {
    cityList: companyCities,
    isCityLoading: loadingCompanyCities,
    cityLoadingError: companyCityError,
  } = useCityList(companyStateId);

  const {
    stateList: contactStates,
    isStateLoading: loadingContactStates,
    stateLoadingError: contactStateError,
  } = useStateList(contactCountryId);

  const {
    cityList: contactCities,
    isCityLoading: loadingContactCities,
    cityLoadingError: contactCityError,
  } = useCityList(contactStateId);

  const companyCountry = countries.find(
    (c) => c.id === Number(companyCountryId)
  );
  const countryIso2 = companyCountry
    ? iso2FromCountryName(companyCountry.name)
    : null;

  const timezoneOptions = useMemo(
    () => getTimezoneOptionsForCountry(countryIso2),
    [countryIso2]
  );

  // ---------- Pricing helpers ----------
  const resetPricing = useCallback(() => {
    setPricingOptions([]);
    setValue("plan", "");
    setValue("billing_frequency", "");
    setValue("plan_price_id", 0);
    setValue("unit_price", "0");
    setValue("total_paid_amount", 0);
  }, [setValue]);

  const mapPlanPrices = (plan?: PlanItem | null): PlanPriceItem[] =>
    (plan?.PlanPrice ?? []).map((pp) => ({
      id: Number(pp.id),
      interval: cap(String(pp.interval)),
      unit_amount: Number(pp.unit_amount),
    }));

  const applyPricingFromOptions = useCallback(
    (
      opts: { id: number; interval: string; unit_amount: number }[],
      licenseCount?: number | null
    ) => {
      setPricingOptions(opts);
      if (opts.length === 0) return;

      const first = opts[0];
      setValue("billing_frequency", first.interval);
      setValue("plan_price_id", first.id, { shouldValidate: true });
      setValue("unit_price", String(first.unit_amount), {
        shouldValidate: true,
      });

      if (licenseCount != null) {
        setValue("total_paid_amount", first.unit_amount * licenseCount);
      }

      trigger([
        "billing_frequency",
        "plan_price_id",
        "unit_price",
        "total_paid_amount",
      ]);
    },
    [setValue, trigger]
  );

  const selectedPrice = useMemo(
    () => pricingOptions.find((p) => p.interval === billingFrequency),
    [pricingOptions, billingFrequency]
  );

  const afterDiscount = useMemo(() => {
    const total = Number(totalPaid || 0);
    const d = Number(discount || 0);
    const discounted = total - (total * d) / 100;
    return Number.isNaN(discounted) ? "0.00" : discounted.toFixed(2);
  }, [totalPaid, discount]);

  // ---------- select plan from license count (for instant autofill) ----------
  const selectPlanFromLicenses = useCallback(
    (licenseCount: number | null) => {
      if (!licenseCount || plans.length === 0) {
        resetPricing();
        return;
      }

      const matched = plans.find((p: PlanItem) => {
        const min = Number(p.min_users ?? 0);
        const max = p.max_users == null ? Infinity : Number(p.max_users);
        return (
          Number(p.status) === 1 && licenseCount >= min && licenseCount <= max
        );
      });

      if (!matched) {
        resetPricing();
        return;
      }

      // matching plan found -> fill values and then trigger so UI refreshes
      setValue("plan", String(matched.id));
      const opts = mapPlanPrices(matched);
      applyPricingFromOptions(opts, licenseCount);
      trigger([
        "plan",
        "billing_frequency",
        "plan_price_id",
        "unit_price",
        "total_paid_amount",
      ]);
    },
    [plans, resetPricing, setValue, applyPricingFromOptions, trigger]
  );

  // ---------- Fetch products ----------
  useEffect(() => {
    (async () => {
      setProductLoading((p) => ({ ...p, products: true }));
      try {
        const productsData = await fetchProducts();
        setProducts(productsData);
      } catch (e: any) {
        dispatch(setError(e?.message || "Failed to load products"));
      } finally {
        setProductLoading((p) => ({ ...p, products: false }));
      }
    })();
  }, [dispatch]);

  // ---------- On product change: fetch plans & reset pricing ----------
  useEffect(() => {
    setLicenses(null);
    setValue("no_of_licenses", undefined as any);

    if (!selectedProductId) {
      setPlans([]);
      resetPricing();
      return;
    }

    (async () => {
      setProductLoading((p) => ({ ...p, plans: true }));
      try {
        const plansData = await fetchMSPPlans(Number(selectedProductId));
        setPlans(plansData);
        resetPricing();
      } catch (e: any) {
        dispatch(setError(e?.message || "Failed to load plans"));
      } finally {
        setProductLoading((p) => ({ ...p, plans: false }));
      }
    })();
  }, [selectedProductId, resetPricing, dispatch, setValue]);

  // ---------- When licenses committed, auto-select plan ----------
  useEffect(() => {
    selectPlanFromLicenses(licenses);
  }, [licenses, selectPlanFromLicenses]);

  // ---------- When plan changed manually, rebuild pricing options ----------
  useEffect(() => {
    if (!selectedPlanId) {
      resetPricing();
      return;
    }
    const plan = plans.find((p) => p.id === Number(selectedPlanId));
    if (!plan) return;
    applyPricingFromOptions(mapPlanPrices(plan), licenses);
  }, [selectedPlanId, plans, licenses, resetPricing, applyPricingFromOptions]);

  // ---------- When frequency changes, sync price ids & totals (immediate trigger) ----------
  useEffect(() => {
    if (!selectedPrice) return;
    setValue("plan_price_id", selectedPrice.id);
    setValue("unit_price", String(selectedPrice.unit_amount));
    if (licenses) {
      setValue(
        "total_paid_amount",
        selectedPrice.unit_amount * Number(licenses || 0)
      );
    }
    trigger(["plan_price_id", "unit_price", "total_paid_amount"]);
  }, [selectedPrice, licenses, setValue, trigger]);

  // ---------- Expiration date ----------
  useEffect(() => {
    if (paymentDate && billingFrequency) {
      const exp = calcExpirationDate(paymentDate, billingFrequency);
      if (exp) setValue("expiration_date", exp);
    }
  }, [paymentDate, billingFrequency, setValue]);

  // ---------- Preselect first region ----------
  useEffect(() => {
    if (regions.length > 0 && companyCountryId) {
      setValue("region_id", regions[0].id);
      trigger("region_id");
    }
  }, [regions, companyCountryId, setValue, trigger]);

  // ---------- Aggregate location errors ----------
  useEffect(() => {
    const combinedError =
      regionLoadingError ||
      countryLoadingError ||
      companyStateError ||
      companyCityError ||
      contactStateError ||
      contactCityError ||
      null;
    if (combinedError) dispatch(setError(combinedError));
    else dispatch(clearError());
  }, [
    regionLoadingError,
    countryLoadingError,
    companyStateError,
    companyCityError,
    contactStateError,
    contactCityError,
    dispatch,
  ]);

  // ---------- Auto clear notifications ----------
  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => dispatch(clearNotification()), 5000);
    return () => clearTimeout(t);
  }, [notification, dispatch]);

  // ---------- Field configs ----------
  const withUnitSuffix = useCallback(
    (watchFn: any) => {
      const freq = watchFn("billing_frequency");
      const sp = pricingOptions.find((p) => p.interval === freq);
      return sp ? (
        <>
          ${sp.unit_amount}/<span className="font-medium">per unit</span>
        </>
      ) : undefined;
    },
    [pricingOptions]
  );

  const productConfigWithAPI = useMemo(() => {
    const isLocked = (licenses ?? 0) > 0;

    return product.map((f) => {
      if (f.name === "product") {
        return {
          ...f,
          options: [
            { label: "-- Choose Product --", value: "" },
            ...products.map((p) => ({
              label: p.product_name,
              value: p.id.toString(),
            })),
          ],
          disabled: productLoading.products,
        };
      }
      if (f.name === "plan") {
        return {
          ...f,
          options: [
            { label: "-- Choose Plan --", value: "" },
            ...plans.map((p) => ({
              label: p.plan_name,
              value: p.id.toString(),
            })),
          ],
          placeholder: licenses
            ? "Auto-selected by license count"
            : "-- Choose Plan --",
          disabled: !selectedProductId || productLoading.plans || isLocked,
        };
      }
      if (f.name === "billing_frequency") {
        return {
          ...f,
          options: [
            { label: "-- Choose Frequency --", value: "" },
            ...pricingOptions.map((p) => ({
              label: p.interval,
              value: p.interval,
            })),
          ],
          disabled:
            (!selectedPlanId && pricingOptions.length === 0) || isLocked,
          suffixText: ({ watch }: { watch: any }) => withUnitSuffix(watch),
        };
      }
      if (f.name === "no_of_licenses") {
        return {
          ...f,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const raw = (e.target.value ?? "").toString().trim();
            const n = Number(raw);
            const lic = Number.isFinite(n) && n > 0 ? n : null;
            setLicenses(lic);
          },
          suffixText: ({ watch }: { watch: any }) => withUnitSuffix(watch),
        };
      }
      return f;
    });
  }, [
    products,
    plans,
    pricingOptions,
    selectedProductId,
    selectedPlanId,
    productLoading,
    licenses,
  ]);

  const companyConfig = useFieldConfig(company, {
    regions,
    countries,
    companyStates,
    companyCities,
    tzOptions: timezoneOptions,
  });
  const contactConfig = useFieldConfig(contact, {
    countries,
    contactStates,
    contactCities,
  });

  // ---------- Submit ----------
  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      try {
        dispatch(setLoading(true));
        dispatch(clearError());

        const price = pricingOptions.find(
          (p) => p.interval === data.billing_frequency
        );
        if (!price) throw new Error("Please select a valid billing frequency");

        const expirationDate =
          data.expiration_date ||
          (data.date_of_payment && data.billing_frequency
            ? calcExpirationDate(data.date_of_payment, data.billing_frequency)
            : "");

        const payload = {
          organization_name: trim(data.organization_name),
          website: trim(data.website),
          timezone: trim(data.timezone),
          region_id: Number(data.region_id),
          plan_price_id: price.id,
          no_of_licenses: data.no_of_licenses,
          total_paid_amount: data.total_paid_amount,
          discount: data.discount,
          tax_id: opt(data.tax_id),
          payment_mode: trim(data.payment_mode),
          transaction_ref: opt(data.transaction_ref),
          comment: opt(data.comment),
          expiration_date: expirationDate,
          date_of_payment: data.date_of_payment,
          cognito_url: import.meta.env.VITE_DEFAULT_COGNITO_URL,
          product_id: Number(data.product),
          plan_id: Number(data.plan),
          contacts: [
            {
              first_name: trim(data.organization_name),
              last_name: "",
              corporate_email: trim(data.company_email),
              designation: "",
              job_title: trim(data.job_title),
              mobile_phone: opt(data.company_phone),
              work_phone_no: opt(data.work_phone_no),
              contact_type: "company",
              is_system_user: false,
              addresses: [
                {
                  street_address: opt(data.company_street_address),
                  country: Number(data.company_country_id),
                  state: Number(data.company_state_id),
                  city: Number(data.company_city_id),
                  postal_code: opt(data.company_postal_code),
                  is_default: true,
                },
              ],
            },
            {
              first_name: trim(data.first_name),
              last_name: trim(data.last_name),
              corporate_email: trim(data.corporate_email),
              designation: trim(data.designation),
              job_title: trim(data.job_title),
              mobile_phone: trim(data.mobile_phone),
              work_phone_no: opt(data.work_phone_no),
              contact_type: "primary",
              is_system_user: true,
              addresses: [
                {
                  street_address: opt(data.street_address),
                  country: Number(data.country_id),
                  state: Number(data.state_id),
                  city: Number(data.city_id),
                  postal_code: opt(data.postal_code),
                  is_default: false,
                },
              ],
            },
          ],
        };

        await createMsp(payload);

        dispatch(
          setNotification({
            message: "MSP created successfully!",
            type: "success",
          })
        );
        reset();
        onSuccess?.();
        onCancel ? onCancel() : navigate("/msps", { replace: true });
      } catch (e: any) {
        const msg = e?.message || "Something went wrong. Please try again.";
        dispatch(setError(msg));
        dispatch(setNotification({ message: msg, type: "error" }));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, reset, onCancel, onSuccess, navigate, pricingOptions]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-6"
        autoComplete="off"
      >
        <FormHeader
          title="Add MSP"
          subtitle="The Smart Way to Add your MSP"
          backLabel="Back to MSP List"
          onBack={onCancel}
        />

        <div className={SECTION_CARD_CLASS}>
          <FormSection title="Company Information" fields={companyConfig} />
          <div className="mt-10">
            <FormSection title="Primary Contact" fields={contactConfig} />
          </div>
          <div className="mt-12">
            <FormSection title="Product" fields={productConfigWithAPI} />
          </div>
        </div>

        <PaymentDetails
          paymentTop={paymentTop}
          paymentDates={paymentDates}
          discountError={formState.errors.discount?.message}
          afterDiscount={afterDiscount}
          discountRegister={register("discount", { valueAsNumber: true })}
        />

        <div className="mt-8 flex justify-center">
          <button
            type="submit"
            disabled={
              loading ||
              isSubmitting ||
              isRegionLoading ||
              isCountryLoading ||
              loadingCompanyStates ||
              loadingCompanyCities ||
              loadingContactStates ||
              loadingContactCities ||
              productLoading.products ||
              productLoading.plans
            }
            className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200 hover:cursor-pointer"
          >
            {loading || isSubmitting ? "CREATING..." : "ADD NOW"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
